import { Controller } from "@hotwired/stimulus";
import {
  formatLocalDate,
  buildConflictDates,
  buildEventMap,
  classifyDate,
  computeOccupancyForRange,
  getBookingWindowBoundary,
  windowMonths,
} from "../lib/calendar_utils";

function getCssVar(name) {
  return getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
}

const PLATFORM_LABELS = {
  airbnb: "Airbnb",
  vrbo: "VRBO",
  furnished_finder: "Furnished Finder",
  direct: "Direct",
  manual: "Manual",
};

// Platforms whose stays are entered by hand; served on one channel and
// coloured separately from the polled Airbnb/VRBO layers.
const OFF_PLATFORM_PLATFORMS = ["furnished_finder", "direct"];

function platformLabel(platform) {
  return PLATFORM_LABELS[platform] || platform;
}

function loadColors() {
  return {
    platform: {
      airbnb: getCssVar("--color-airbnb"),
      vrbo: getCssVar("--color-vrbo"),
      furnished_finder: getCssVar("--color-furnished_finder"),
      direct: getCssVar("--color-direct"),
      manual: getCssVar("--color-manual"),
      both: getCssVar("--color-both-platforms"),
      unblocked: getCssVar("--color-unblocked"),
    },
    type: {
      booking: getCssVar("--color-booking"),
      tentative: getCssVar("--color-tentative"),
      block: getCssVar("--color-block"),
      unknown: getCssVar("--color-unknown"),
    },
  };
}

export default class extends Controller {
  static values = {
    eventsUrl: String,
    year: Number,
    platform: String,
    pageUrl: String,
    // "year" (default) shows a calendar year; "rolling" shows twelve months
    // from `start` (YYYY-MM, default the current month).
    view: String,
    start: String,
  };

  connect() {
    this.year = this.yearValue || new Date().getFullYear();
    this.view = this.viewValue === "rolling" ? "rolling" : "year";
    const now = new Date();
    const [startYear, startMonth] = /^\d{4}-\d{2}$/.test(this.startValue)
      ? this.startValue.split("-").map(Number)
      : [now.getFullYear(), now.getMonth() + 1];
    this.startYear = startYear;
    this.startMonth = startMonth - 1;
    this.activePlatform = this.platformValue || "";
    this.colors = loadColors();
    this.fetchAndRender();
  }

  // The twelve {year, month} pairs on screen.
  months() {
    return this.view === "rolling"
      ? windowMonths(this.startYear, this.startMonth)
      : windowMonths(this.year, 0);
  }

  // First night shown and the night after the last one shown.
  windowRange() {
    const months = this.months();
    const first = months[0];
    const last = months[months.length - 1];
    return {
      start: new Date(first.year, first.month, 1),
      end: new Date(last.year, last.month + 1, 1),
    };
  }

  monthLabel({ year, month }) {
    return new Date(year, month).toLocaleString("default", {
      month: "short",
      year: "numeric",
    });
  }

  windowTitle() {
    if (this.view === "year") return String(this.year);
    const months = this.months();
    return `${this.monthLabel(months[0])} – ${this.monthLabel(months[months.length - 1])}`;
  }

  isCurrentMonthStart() {
    const now = new Date();
    return (
      this.startYear === now.getFullYear() && this.startMonth === now.getMonth()
    );
  }

  async fetchAndRender() {
    const [airbnb, vrbo, manual, offPlatform, conflicts, meta] =
      await Promise.all([
        this.fetchEvents("airbnb"),
        this.fetchEvents("vrbo"),
        this.fetchEvents("manual"),
        this.fetchEvents("off_platform"),
        this.fetchEvents("conflicts"),
        this.fetchMeta(),
      ]);

    const conflictDates = buildConflictDates(conflicts);
    const eventMap = buildEventMap(airbnb, vrbo, manual, offPlatform);
    this.bookingWindowBoundary = getBookingWindowBoundary(
      airbnb,
      vrbo,
      meta.booking_windows,
      this.activePlatform,
    );
    this.detectedWindows = meta.detected_windows || {};
    this.render(eventMap, conflictDates);
  }

  async fetchEvents(platform) {
    try {
      const response = await fetch(
        `${this.eventsUrlValue}?platform=${platform}`,
      );
      return await response.json();
    } catch {
      return [];
    }
  }

  async fetchMeta() {
    try {
      const response = await fetch(`${this.eventsUrlValue}/meta`);
      return await response.json();
    } catch {
      return { booking_windows: {} };
    }
  }

  render(eventMap, conflictDates) {
    const container = document.createElement("div");
    container.className = "year-calendar";

    // Header: window nav, view toggle, occupancy
    const header = document.createElement("div");
    header.className = "d-flex align-items-center gap-2 mb-2 flex-wrap";
    header.append(this.navButton("prev", "\u2039"));
    const title = document.createElement("strong");
    title.textContent = this.windowTitle();
    header.append(title);
    header.append(this.navButton("next", "\u203a"));
    header.append(this.renderViewToggle());
    header.append(this.renderOccupancy(eventMap));
    container.append(header);

    // Month grid
    const grid = document.createElement("div");
    grid.className = "year-calendar-grid";
    for (const { year, month } of this.months()) {
      grid.append(this.renderMonth(year, month, eventMap, conflictDates));
    }
    container.append(grid);

    // Legend
    container.append(this.renderLegend());

    this.element.replaceChildren(container);
  }

  // Right-aligned in the header so the figure sits beside the year it
  // describes, computed from the same event map the grid is painted from.
  // The current year reports realized occupancy (nights through last night),
  // the number OTA dashboards report and past years can be compared with; the
  // whole-year figure, confirmed future bookings included, goes in the tooltip.
  renderOccupancy(eventMap) {
    const { start, end } = this.windowRange();
    const occupancy = computeOccupancyForRange(
      eventMap,
      start,
      end,
      this.activePlatform,
    );
    // A rolling window is mostly ahead of us, so it reports what is sold.
    const realized =
      this.view === "year" &&
      this.year === new Date().getFullYear() &&
      occupancy.elapsedPercent !== null;

    const stat = document.createElement("span");
    stat.className = "ms-auto small text-muted year-calendar-occupancy";
    stat.textContent = realized
      ? `${occupancy.elapsedPercent}% booked so far · ${occupancy.elapsedBooked} of ${occupancy.elapsed} nights`
      : `${occupancy.percent}% booked · ${occupancy.booked} of ${occupancy.nights} nights`;

    const scope = this.activePlatform
      ? `a confirmed ${platformLabel(this.activePlatform)} booking`
      : "a confirmed booking on any platform; a night booked on two platforms counts once";
    let span;
    if (this.view === "rolling") {
      span = `Nights from ${this.windowTitle()}`;
    } else if (realized) {
      span = `Nights in ${this.year} through last night`;
    } else {
      span = `Nights in ${this.year}`;
    }
    const lines = [
      `${span} with ${scope}. Blocks and tentative holds are not counted.`,
    ];
    if (realized) {
      lines.push(
        `Whole year, confirmed bookings included: ${occupancy.percent}% (${occupancy.booked} of ${occupancy.nights} nights).`,
      );
    }
    if (occupancy.blockedOnly > 0) {
      lines.push(
        `${occupancy.blockedOnly} nights in view are blocked with no recorded booking. ` +
          "If a guest stayed off-platform, record the stay so it counts.",
      );
    }
    stat.title = lines.join("\n");
    return stat;
  }

  // Calendar year, or the twelve months from the current one. Switching to
  // the rolling view always starts it at this month.
  renderViewToggle() {
    const group = document.createElement("div");
    group.className = "btn-group btn-group-sm ms-2";
    group.setAttribute("role", "group");
    group.setAttribute("aria-label", "Calendar view");
    const options = [
      ["year", "Year"],
      ["rolling", "Next 12 months"],
    ];
    for (const [view, label] of options) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = `btn ${this.view === view ? "btn-secondary" : "btn-outline-secondary"}`;
      btn.textContent = label;
      btn.setAttribute("aria-pressed", String(this.view === view));
      btn.addEventListener("click", () => {
        if (this.view === view) return;
        this.view = view;
        if (view === "rolling") {
          const now = new Date();
          this.startYear = now.getFullYear();
          this.startMonth = now.getMonth();
        }
        this.updateUrl();
        this.fetchAndRender();
      });
      group.append(btn);
    }
    return group;
  }

  renderLegend() {
    const legend = document.createElement("div");
    legend.className = "year-calendar-legend mt-2";

    const addItem = (container, item) => {
      const span = document.createElement("span");
      span.className = "year-cal-legend-item";
      const dot = document.createElement("span");
      dot.className = "year-cal-dot";
      if (item.striped) {
        dot.style.background =
          "repeating-linear-gradient(135deg, #d5d5d5, #d5d5d5 2px, #e8e8e8 2px, #e8e8e8 4px)";
      } else if (item.split) {
        dot.style.background = `linear-gradient(135deg, ${item.split[0]} 50%, ${item.split[1]} 50%)`;
      } else {
        dot.style.backgroundColor = item.color;
      }
      if (item.pulseClass) dot.classList.add(item.pulseClass);
      span.append(dot);
      span.append(document.createTextNode(` ${item.label}`));
      container.append(span);
    };

    const addSection = (label, items) => {
      const section = document.createElement("span");
      section.className = "year-cal-legend-section";
      if (label) {
        const lbl = document.createElement("strong");
        lbl.textContent = label + ": ";
        lbl.style.fontSize = "0.7rem";
        section.append(lbl);
      }
      for (const item of items) addItem(section, item);
      legend.append(section);
    };

    // How to read the split cell
    addSection("", [
      {
        split: [this.colors.platform.airbnb, this.colors.type.booking],
        label: "Platform \u25E4 / Type \u25E2",
      },
    ]);

    // Platform colors
    if (this.activePlatform) {
      const otherPlatform = {
        airbnb: "VRBO",
        vrbo: "Airbnb",
      }[this.activePlatform];
      addSection("Platform \u25E4", [
        {
          color: this.colors.platform[this.activePlatform],
          label: platformLabel(this.activePlatform),
        },
        {
          color: "#d5d5d5",
          label: `Unavailable (${otherPlatform || "other platforms"})`,
          striped: true,
        },
      ]);
    } else {
      addSection("Platform \u25E4", [
        { color: this.colors.platform.airbnb, label: "Airbnb" },
        { color: this.colors.platform.vrbo, label: "VRBO" },
        ...OFF_PLATFORM_PLATFORMS.map((platform) => ({
          color: this.colors.platform[platform],
          label: platformLabel(platform),
        })),
      ]);
    }

    // Type colors
    addSection("Type \u25E2", [
      { color: this.colors.type.booking, label: "Booking" },
      { color: this.colors.type.tentative, label: "Tentative" },
      { color: this.colors.type.block, label: "Block" },
    ]);

    // Conflicts override the platform side
    addSection("Conflicts \u25E4", [
      {
        color: this.colors.platform.both,
        label: "Double Booking",
        pulseClass: "year-cal-conflict-double",
      },
      {
        color: this.colors.platform.unblocked,
        label: "Unblocked",
        pulseClass: "year-cal-conflict-unblocked",
      },
    ]);

    addSection("", [
      {
        striped: true,
        label:
          this.bookingWindowBoundary?.source === "booking_window"
            ? "Beyond booking window"
            : "Beyond calendar horizon",
      },
    ]);

    return legend;
  }

  navButton(direction, label) {
    const btn = document.createElement("button");
    btn.className = "btn btn-sm btn-outline-secondary";
    btn.textContent = label;
    btn.addEventListener("click", () => {
      const step = direction === "next" ? 1 : -1;
      if (this.view === "rolling") {
        const moved = new Date(this.startYear, this.startMonth + step, 1);
        this.startYear = moved.getFullYear();
        this.startMonth = moved.getMonth();
      } else {
        this.year += step;
      }
      this.updateUrl();
      this.fetchAndRender();
    });
    return btn;
  }

  // The window lives in the URL (`year`, or `view=rolling` + `start`) so a
  // reload, the platform filter buttons and the table's links all keep it.
  // Defaults are left out so the plain page URL stays plain.
  applyWindowParams(url) {
    const now = new Date();
    if (this.view === "rolling") {
      url.searchParams.delete("year");
      url.searchParams.set("view", "rolling");
      if (this.isCurrentMonthStart()) {
        url.searchParams.delete("start");
      } else {
        url.searchParams.set(
          "start",
          `${this.startYear}-${String(this.startMonth + 1).padStart(2, "0")}`,
        );
      }
    } else {
      url.searchParams.delete("view");
      url.searchParams.delete("start");
      if (this.year === now.getFullYear()) {
        url.searchParams.delete("year");
      } else {
        url.searchParams.set("year", this.year);
      }
    }
    return url;
  }

  updateUrl() {
    const url = this.applyWindowParams(new URL(window.location));
    history.replaceState(null, "", url);
    this.syncWindowToPageLinks();
  }

  syncWindowToPageLinks() {
    const basePath = new URL(this.pageUrlValue, window.location.origin)
      .pathname;

    for (const link of document.querySelectorAll(`a[href^="${basePath}"]`)) {
      link.href = this.applyWindowParams(
        new URL(link.href, window.location.origin),
      ).toString();
    }

    for (const form of document.querySelectorAll(
      `form[action^="${basePath}"]`,
    )) {
      form.action = this.applyWindowParams(
        new URL(form.action, window.location.origin),
      ).toString();
    }
  }

  renderMonth(year, monthIndex, eventMap, conflictDates) {
    const div = document.createElement("div");
    div.className = "year-calendar-month";

    const title = document.createElement("div");
    title.className = "year-calendar-month-title";
    // A rolling window spans two years, so each month names its year.
    title.textContent = new Date(year, monthIndex).toLocaleString(
      "default",
      this.view === "rolling"
        ? { month: "short", year: "numeric" }
        : { month: "short" },
    );
    div.append(title);

    // Day headers
    const dayHeaders = document.createElement("div");
    dayHeaders.className = "year-calendar-days";
    for (const d of ["S", "M", "T", "W", "T", "F", "S"]) {
      const dh = document.createElement("span");
      dh.className = "year-cal-header";
      dh.textContent = d;
      dayHeaders.append(dh);
    }
    div.append(dayHeaders);

    // Day cells
    const grid = document.createElement("div");
    grid.className = "year-calendar-days";

    const firstDay = new Date(year, monthIndex, 1).getDay();
    for (let i = 0; i < firstDay; i++) {
      const empty = document.createElement("span");
      empty.className = "year-cal-day year-cal-empty";
      grid.append(empty);
    }

    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
    const today = formatLocalDate(new Date());

    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(monthIndex + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      const info = eventMap[dateStr];

      const cell = document.createElement("span");
      cell.className = "year-cal-day";
      cell.textContent = String(d);

      if (dateStr === today) {
        cell.classList.add("year-cal-today");
      }

      const classified = classifyDate(
        info,
        conflictDates[dateStr],
        this.activePlatform,
        dateStr,
        this.bookingWindowBoundary,
      );

      if (classified.type === "active") {
        const platformColor = classified.isDoubleBooking
          ? this.colors.platform.both
          : classified.isUnblocked
            ? this.colors.platform.unblocked
            : this.getPlatformColor(classified.platforms);
        const typeColor = this.getTypeColor(classified.eventTypes);

        cell.style.background = `linear-gradient(135deg, ${platformColor} 50%, ${typeColor} 50%)`;
        cell.style.color = "#fff";
        cell.title = this.buildTooltip(dateStr, classified);

        if (classified.isDoubleBooking)
          cell.classList.add("year-cal-conflict-double");
        else if (classified.isUnblocked)
          cell.classList.add("year-cal-conflict-unblocked");
        if (classified.isStart) cell.classList.add("year-cal-start");
        if (classified.isEnd) cell.classList.add("year-cal-end");
      } else if (classified.type === "availability-window") {
        cell.classList.add("year-cal-avail-window");
        cell.title = this.buildTooltip(dateStr, classified);
      } else if (classified.type === "unavailable") {
        cell.classList.add("year-cal-unavailable");
        cell.title = this.buildTooltip(dateStr, classified);
      } else if (classified.type === "conflict-only") {
        const color =
          classified.conflictType === "double_booking"
            ? this.colors.platform.both
            : this.colors.platform.unblocked;
        cell.style.background = color;
        cell.style.color = "#fff";
        cell.title = this.buildTooltip(dateStr, classified);
        cell.classList.add(
          classified.conflictType === "double_booking"
            ? "year-cal-conflict-double"
            : "year-cal-conflict-unblocked",
        );
      }

      grid.append(cell);
    }

    div.append(grid);
    return div;
  }

  getPlatformColor(platforms) {
    if (platforms.includes("airbnb") && platforms.includes("vrbo")) {
      return this.colors.platform.both;
    }
    // An off-platform stay is normally held by a native OTA block over the
    // same dates. The paid stay is the thing worth seeing, so it outranks the
    // block that merely holds it.
    const offPlatform = platforms.find((p) =>
      OFF_PLATFORM_PLATFORMS.includes(p),
    );
    if (offPlatform) return this.colors.platform[offPlatform];
    return this.colors.platform[platforms[0]] || "#999";
  }

  getTypeColor(types) {
    if (types.includes("booking") && types.includes("block")) {
      return this.colors.type.booking;
    }
    if (types.includes("booking")) return this.colors.type.booking;
    if (types.includes("tentative")) return this.colors.type.tentative;
    if (types.includes("block")) return this.colors.type.block;
    return this.colors.type.unknown;
  }

  buildTooltip(dateStr, classified) {
    const lines = [dateStr];
    const titles = classified.platformTitles || {};
    const pTypes = classified.platformTypes || {};

    if (classified.type === "conflict-only") {
      lines.push(
        "",
        classified.conflictType === "double_booking"
          ? "\u26a0 DOUBLE BOOKING"
          : "\u26a0 UNBLOCKED",
      );
      lines.push(
        classified.conflictType === "double_booking"
          ? "Booked on both Airbnb and VRBO"
          : "Booking exists but not blocked on other platform",
      );
      return lines.join("\n");
    }

    // List each platform's events (and any user-set notes alongside)
    const allPlatforms = [
      "airbnb",
      "vrbo",
      "manual",
      ...OFF_PLATFORM_PLATFORMS,
    ];
    const notes = classified.platformNotes || {};
    for (const p of allPlatforms) {
      const pTitles = titles[p];
      if (pTitles && pTitles.length > 0) {
        for (const t of pTitles) {
          lines.push(`  ${t}`);
        }
      }
      const pNotes = notes[p];
      if (pNotes && pNotes.length > 0) {
        for (const n of pNotes) {
          lines.push(`    → ${n}`);
        }
      }
    }

    // Conflict explanation
    if (classified.isDoubleBooking) {
      lines.push("");
      lines.push("\u26a0 DOUBLE BOOKING");
      const airbnbTypes = pTypes.airbnb || [];
      const vrboTypes = pTypes.vrbo || [];
      lines.push(
        `Airbnb has: ${airbnbTypes.join(", ")}`,
        `VRBO has: ${vrboTypes.join(", ")}`,
        "Both platforms have booking/tentative events on this date",
      );
    } else if (classified.isUnblocked) {
      lines.push("");
      lines.push("\u26a0 UNBLOCKED");
      const booked = classified.platforms;
      const missing = ["airbnb", "vrbo"].filter((p) => !booked.includes(p));
      lines.push(
        `Booked on: ${booked.join(", ")}`,
        `Not blocked on: ${missing.join(", ")}`,
      );
    }

    if (classified.hasAvailabilityWindow) {
      lines.push("");
      const dw = this.activePlatform
        ? this.detectedWindows[this.activePlatform] || {}
        : {};
      if (dw.advance_notice) {
        lines.push(
          `\u2139 Advance Notice (${dw.advance_notice}) \u2014 minimum lead time for bookings`,
        );
      }
      if (dw.calendar_horizon) {
        lines.push(
          `\u2139 Calendar Horizon (${dw.calendar_horizon}) \u2014 edge of platform's iCal feed range`,
        );
      }
      if (!dw.advance_notice && !dw.calendar_horizon) {
        lines.push("\u2139 Availability window \u2014 not a real block");
      }
    }

    if (classified.type === "availability-window") {
      lines.push("");
      const dw = this.activePlatform
        ? this.detectedWindows[this.activePlatform] || {}
        : {};
      const wt = classified.windowTypes || {};
      const boundary = this.bookingWindowBoundary;
      // Use per-event window type when available
      if (wt.advance_notice) {
        lines.push(
          `Advance Notice (${dw.advance_notice || wt.advance_notice}) \u2014 guests must book at least this far ahead`,
        );
      } else if (boundary?.source === "booking_window") {
        lines.push(
          `Beyond ${classified.platforms.join("/")} booking window (${boundary.label}) \u2014 guests cannot book this far in advance`,
        );
      } else {
        const horizon = dw.calendar_horizon || wt.calendar_horizon || "";
        const suffix = horizon ? ` (${horizon})` : "";
        lines.push(
          `Beyond ${classified.platforms.join("/")} calendar horizon${suffix} \u2014 feed doesn't extend this far`,
        );
      }
    }

    if (classified.type === "unavailable") {
      lines.push("");
      lines.push(
        `Unavailable \u2014 booked on ${classified.platforms.join(", ")}`,
      );
    }

    return lines.join("\n");
  }
}
